import { FC, useMemo } from 'react';
import { useFATCATaxResidencies } from '../../../../../utils/useFATCATaxResidencies';
import { FATCATranslations } from '../../../../../utils/getFATCATranslations';
import { motion } from 'framer-motion';
import { css } from '@bts-web/utils-style-engine';
import { CheckboxToggle } from '../../../../../../common';
import { BorderedTextInput } from '../../../../reusable/FATCAInputs/BorderedTextInput/BorderedTextInput';
import { RegularClose } from '../../../../../../common/svgIcons';

const FATCATaxResidencyVerticalEntry: FC<{
  fatcaTaxResidencies: ReturnType<typeof useFATCATaxResidencies>;
  translations: FATCATranslations;
  isWithinList?: boolean;
  id: string;
}> = ({ fatcaTaxResidencies, translations, isWithinList, id }) => {
  const WrapperElement = isWithinList ? motion.li : motion.div;

  const currentItemData = useMemo(() => {
    return fatcaTaxResidencies.fatcaTaxResidencies.find(
      (entry) => entry.id === id,
    );
  }, [fatcaTaxResidencies, id]);

  if (!currentItemData) {
    return null;
  }

  return (
    <WrapperElement
      className={css({
        padding: 'extra_small',
        display: 'flex',
        flexDirection: 'column',
        gap: 'small',
        border: '1px solid rgba(0, 0, 0, 0.1)',
        borderRadius: '8px',
      })}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <div
        className={css({
          display: 'flex',
          alignItems: 'center',
          gap: 'small',
          justifyContent: 'space-between',
        })}
      >
        <h6>{translations.section_title_tax_residency}</h6>
        {fatcaTaxResidencies.fatcaTaxResidencies.length > 1 && (
          <button
            className={css({
              p: 'extra_small',
              borderRadius: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: 'transparent',
              border: 'none',
              cursor: 'pointer',
            })}
            onClick={() => {
              fatcaTaxResidencies.removeFATCATaxResidency(currentItemData.id);
            }}
          >
            <RegularClose size={16} />
          </button>
        )}
      </div>
      <div>
        <BorderedTextInput
          placeholder={translations.country}
          type="text"
          name={`country-for-${currentItemData?.id}`}
          value={currentItemData.countryValue || ''}
          onChange={(e) => {
            fatcaTaxResidencies.updateFATCATaxResidency(currentItemData.id, {
              countryValue: e.target.value,
            });
          }}
        />
      </div>
      <div
        className={css({
          display: 'flex',
          gap: 'small',
        })}
      >
        <label
          className={css({
            display: 'flex',
            flexDirection: 'column',
            gap: 'extra_small',
            flex: 1,
          })}
        >
          <div>{translations.tin_code_label}</div>{' '}
          <BorderedTextInput
            disabled={currentItemData.tinAbsent}
            placeholder="0000000000000"
            type="number"
            name={`tin-number-for-${currentItemData?.id}`}
            value={currentItemData.tinCode || ''}
            onChange={(e) => {
              fatcaTaxResidencies.updateFATCATaxResidency(currentItemData.id, {
                tinCode: e.target.value,
              });
            }}
          />
        </label>

        <label
          style={{
            maxWidth: '200px',
          }}
          className={css({
            display: 'flex',
            alignItems: 'center',
            gap: 'small',
            fontSize: 'caption.small',
            fontWeight: 'caption.small',
            lineHeight: 'caption.small',
            letterSpacing: 'caption.small',
            cursor: 'pointer',
          })}
          htmlFor={`tin-absent-checkbox-for-${currentItemData?.id}`}
        >
          <CheckboxToggle
            id={`tin-absent-checkbox-for-${currentItemData?.id}`}
            checked={currentItemData.tinAbsent}
            onChange={(e) => {
              fatcaTaxResidencies.updateFATCATaxResidency(currentItemData.id, {
                tinAbsent: e.target.checked,
                tinCode: e.target.checked ? null : currentItemData.tinCode,
              });
            }}
          />
          <div>{translations.tin_code_absent}</div>
        </label>
      </div>
    </WrapperElement>
  );
};

export { FATCATaxResidencyVerticalEntry };
